(function () {
  const TOOL_SELECTOR = 'fa-round-product-tools';
  const PREVIEW_SELECTOR = '[data-fa-round-preview]';

  class RoundProductTools extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = this.readConfig();
      if (!this.config) return;

      this.locale = String(this.config.locale || document.documentElement.lang || 'de-CH');
      this.i18n = { ...(this.config.i18n || {}) };
      this.variants = Array.isArray(this.config.variants) ? this.config.variants : [];
      this.currentVariant = this.findVariant(this.config.variantId) || this.variants[0] || null;
      this.deliveryText = '';
      this.deliveryError = false;
      this.deliveryRequestId = 0;
      this.deliveryController = null;
      this.pendingVariantId = null;

      this.section = this.closest('.shopify-section');
      this.variantUpdateHandler = (event) => this.handleVariantUpdate(event);
      this.optimisticChangeHandler = (event) => this.handleOptimisticChange(event);
      this.resizeHandler = () => this.renderPreviews();

      this.section?.addEventListener('variant:update', this.variantUpdateHandler);
      this.section?.addEventListener('change', this.optimisticChangeHandler, true);
      window.addEventListener('resize', this.resizeHandler, { passive: true });

      this.bindStickyBuyBars();
      this.renderAll();
      this.requestDeliveryEstimate();
    }

    disconnectedCallback() {
      this.section?.removeEventListener('variant:update', this.variantUpdateHandler);
      this.section?.removeEventListener('change', this.optimisticChangeHandler, true);
      window.removeEventListener('resize', this.resizeHandler);
      this.deliveryController?.abort();
    }

    readConfig() {
      const script = this.querySelector('[data-fa-round-config]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent || '{}');
      } catch {
        return null;
      }
    }

    findVariant(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    handleOptimisticChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.matches('.fa-round-variant-card input[data-variant-id]') || input.disabled) return;

      const nextVariant = this.findVariant(input.dataset.variantId);
      if (!nextVariant) return;

      this.currentVariant = nextVariant;
      this.pendingVariantId = String(nextVariant.id);
      this.resetDelivery();
      this.syncSelectedCard(input);
      this.renderAll();
      this.updateMainPrice(nextVariant);
    }

    handleVariantUpdate(event) {
      const detail = event.detail || {};
      const productId = detail.data?.productId;
      if (productId && String(productId) !== String(this.config.productId)) return;

      const nextVariant = this.findVariant(detail.resource?.id);
      if (!nextVariant) return;

      this.currentVariant = nextVariant;
      this.pendingVariantId = null;
      this.resetDelivery();
      this.renderAll();
      this.updateMainPrice(nextVariant);
      this.requestDeliveryEstimate();
    }

    resetDelivery() {
      this.deliveryRequestId += 1;
      this.deliveryController?.abort();
      this.deliveryController = null;
      this.deliveryText = '';
      this.deliveryError = false;
    }

    syncSelectedCard(selectedInput) {
      this.section?.querySelectorAll('.fa-round-variant-card input[data-variant-id]').forEach((input) => {
        input.closest('.fa-round-variant-card')?.classList.toggle('is-selected', input === selectedInput);
      });
    }

    renderAll() {
      if (!this.currentVariant) return;
      this.renderPanel();
      this.renderPreviews();
      this.updateDynamicText();
      this.updateStickyBuyBars();
      this.updatePurchaseAvailability();
    }

    renderPanel() {
      const panel = this.querySelector('[data-fa-round-panel]');
      if (!panel || !this.currentVariant) return;

      const diameter = this.parseDiameter(this.currentVariant.title);
      panel.innerHTML = `
        <div class="fa-round-tools">
          <section class="fa-round-selected" aria-label="${this.escapeHtml(this.config.cardTitle)}">
            <div class="fa-round-selected__choice">
              <span>${this.escapeHtml(this.config.cardTitle)}</span>
              <strong>Ø ${this.escapeHtml(diameter)} cm</strong>
            </div>
            <ul class="fa-round-selected__specs">
              <li><b>Ø ${this.escapeHtml(diameter)} cm</b><span>${this.escapeHtml(this.t('diameter'))}</span></li>
              <li><b>${this.escapeHtml(this.config.depthMm)} mm</b><span>${this.escapeHtml(this.t('depth'))}</span></li>
            </ul>
          </section>
          <div class="fa-round-logistics">
            <section class="fa-round-stock fa-round-stock--production" aria-label="${this.escapeHtml(this.t('availability'))}">
              <div class="fa-round-stock__title">
                <span class="fa-round-signal" aria-hidden="true"></span>
                <strong>${this.escapeHtml(this.t('productionItem'))}</strong>
              </div>
              <p>${this.escapeHtml(this.t('productionTiming'))}</p>
            </section>
            <section class="fa-round-delivery" aria-live="polite">
              <span>${this.escapeHtml(this.t('estimatedDelivery'))}</span>
              <strong>${this.escapeHtml(this.deliveryStatusText())}</strong>
            </section>
          </div>
        </div>
      `;
    }

    renderPreviews() {
      if (!this.currentVariant) return;
      const diameter = this.parseDiameter(this.currentVariant.title);
      const previewSize = window.matchMedia('(max-width: 749px)').matches ? 218 : 286;
      const rail = Math.max(8, Math.round(previewSize * 0.048));
      const markup = `
        <section class="fa-round-preview" aria-label="${this.escapeHtml(this.t('previewAria'))}">
          <div class="fa-round-preview__meta">
            <strong>${this.escapeHtml(this.t('previewTitle'))}</strong>
            <span>Ø ${this.escapeHtml(diameter)} cm · ${this.escapeHtml(this.config.depthMm)} mm ${this.escapeHtml(this.t('depth'))}</span>
            <span>${this.escapeHtml(this.t('roundShape'))}</span>
          </div>
          <div class="fa-round-preview__stage">
            <div class="fa-round-preview__measured" style="--preview-size:${previewSize}px;--preview-rail:${rail}px">
              <div class="fa-round-preview__disc"></div>
              <span class="fa-round-preview__diameter"><b>Ø ${this.escapeHtml(diameter)} cm</b></span>
            </div>
          </div>
        </section>
      `;

      document.querySelectorAll(`${PREVIEW_SELECTOR}[data-product-id="${this.config.productId}"]`).forEach((preview) => {
        preview.innerHTML = markup;
      });
    }

    updateDynamicText() {
      const diameter = this.parseDiameter(this.currentVariant.title);
      document.querySelectorAll('[data-fa-round-page-title]').forEach((element) => {
        element.textContent = `${this.config.pageTitle} · Ø ${diameter} cm`;
      });
      document.querySelectorAll('[data-fa-round-diameter]').forEach((element) => {
        element.textContent = `Ø ${diameter} cm`;
      });
    }

    updateMainPrice(variant) {
      const priceContainer = this.section?.querySelector('.product-details [ref="priceContainer"]');
      if (!priceContainer) return;

      const price = this.formatMoney(variant.price);
      const compareAtPrice = this.readNumber(variant.compareAtPrice, 0);
      const hasComparePrice = compareAtPrice > this.readNumber(variant.price, 0);

      priceContainer.querySelectorAll('.price__regular .price, .price__sale .price-item--sale').forEach((element) => {
        element.textContent = price;
      });
      priceContainer.querySelectorAll('.price__sale .compare-at-price').forEach((element) => {
        element.textContent = hasComparePrice ? this.formatMoney(compareAtPrice) : '';
      });
      priceContainer.querySelector('.price__regular')?.classList.toggle('price__hidden', hasComparePrice);
      priceContainer.querySelector('.price__sale')?.classList.toggle('price__hidden', !hasComparePrice);
    }

    stockState() {
      const quantity = this.readNumber(this.currentVariant?.inventoryQuantity, 0);
      if (!this.isInStock()) return { key: 'out', label: this.t('outOfStock') };
      if (quantity < 5) return { key: 'low', label: this.t('lowStock') };
      if (quantity <= 10) return { key: 'in', label: this.t('inStock') };
      return { key: 'very', label: this.t('veryAvailable') };
    }

    isInStock() {
      return Boolean(this.currentVariant?.available) && this.readNumber(this.currentVariant?.inventoryQuantity, 0) > 0;
    }

    deliveryStatusText() {
      if (!this.isInStock()) return this.t('byAvailability');
      if (this.deliveryText) return this.deliveryText;
      if (this.deliveryError) return this.t('deliveryReload');
      if (!this.config.deliveryEstimateUrl) return this.t('deliveryUnavailable');
      return this.t('calculating');
    }

    async requestDeliveryEstimate() {
      const variant = this.currentVariant;
      if (!variant || !this.isInStock() || !this.config.deliveryEstimateUrl) return;

      const requestId = ++this.deliveryRequestId;
      const controller = new AbortController();
      this.deliveryController = controller;
      const timeout = window.setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch(this.config.deliveryEstimateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            locale: this.locale,
            sku: variant.sku || '',
            thicknessCm: this.config.thicknessCm,
            productGroup: 'round',
            inventoryQuantity: this.readNumber(variant.inventoryQuantity, 0),
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) throw new Error(payload.message || 'Delivery estimate failed');
        if (requestId !== this.deliveryRequestId || String(variant.id) !== String(this.currentVariant?.id)) return;
        this.deliveryText = String(payload.estimate?.deliveryText || '').trim() || this.t('byAvailability');
      } catch (error) {
        if (requestId !== this.deliveryRequestId) return;
        if (error?.name === 'AbortError' && controller !== this.deliveryController) return;
        this.deliveryError = true;
      } finally {
        window.clearTimeout(timeout);
        if (requestId === this.deliveryRequestId) {
          this.deliveryController = null;
          this.renderPanel();
        }
      }
    }

    bindStickyBuyBars() {
      document.querySelectorAll(`[data-fa-round-sticky-bar][data-product-id="${this.config.productId}"]`).forEach((bar) => {
        if (bar.dataset.faRoundBound === 'true') return;
        bar.dataset.faRoundBound = 'true';
        bar.querySelector('[data-fa-round-sticky-cart]')?.addEventListener('click', () => {
          const form = document.querySelector(`product-form-component[data-product-id="${this.config.productId}"]`);
          form?.querySelector('.add-to-cart-button:not(:disabled)')?.click();
        });
      });
    }

    updateStickyBuyBars() {
      const diameter = this.parseDiameter(this.currentVariant.title);
      const available = this.pendingVariantId === null && this.isInStock();
      document.querySelectorAll(`[data-fa-round-sticky-bar][data-product-id="${this.config.productId}"]`).forEach((bar) => {
        bar.classList.add('is-visible');
        const title = bar.querySelector('[data-fa-round-sticky-title]');
        const price = bar.querySelector('[data-fa-round-sticky-price]');
        const button = bar.querySelector('[data-fa-round-sticky-cart]');
        if (title) title.textContent = `${this.config.pageTitle} · Ø ${diameter} cm`;
        if (price) price.textContent = this.formatMoney(this.currentVariant.price);
        if (button) button.disabled = !available;
        bar.toggleAttribute('aria-busy', this.pendingVariantId !== null);
      });
    }

    updatePurchaseAvailability() {
      const available = this.pendingVariantId === null && this.isInStock();
      const nativeStickyButton = this.section
        ?.querySelector(`sticky-add-to-cart[data-product-id="${this.config.productId}"]`)
        ?.querySelector('.sticky-add-to-cart__button');
      if (nativeStickyButton) nativeStickyButton.disabled = !available;
    }

    parseDiameter(title) {
      const match = String(title || '').match(/(\d+(?:[.,]\d+)?)/);
      return match ? match[1].replace(',', '.') : '';
    }

    formatMoney(cents) {
      const currency = String(this.config.currency || 'CHF').toUpperCase();
      try {
        return new Intl.NumberFormat(this.locale, { style: 'currency', currency }).format(this.readNumber(cents, 0) / 100);
      } catch {
        return `${currency} ${(this.readNumber(cents, 0) / 100).toFixed(2)}`;
      }
    }

    readNumber(value, fallback) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : fallback;
    }

    t(key) {
      return this.i18n[key] || key;
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get(TOOL_SELECTOR)) {
    customElements.define(TOOL_SELECTOR, RoundProductTools);
  }
})();
